import { get, type PathAsString } from 'cross-state';

type Direction = 'asc' | 'desc';
type Selector<T> = (value: T) => unknown;
type MaybeArray<T> = T | T[];

const typeOrder = ['boolean', 'number', 'string', 'object', 'undefined'];

export default function orderBy<T>(
  array: T[],
  selector: MaybeArray<PathAsString<T> | Selector<T>> = (x) => x,
  direction: Direction | Direction[] = 'asc',
) {
  selector = Array.isArray(selector) ? selector : [selector];
  const _selectors = selector.map((selector) =>
    selector instanceof Function ? selector : (x: T) => get(x, selector as any),
  );

  return array.slice().sort((a, b) => {
    let index = 0;
    for (const s of _selectors) {
      const d = (Array.isArray(direction) ? (direction[index] ?? 'asc') : direction) === 'asc' ? 1 : -1;
      index++;

      const av = normalize(s(a));
      const bv = normalize(s(b));

      if (av === bv) {
        continue;
      }

      if (typeof av === 'string' && typeof bv === 'string') {
        return av.localeCompare(bv) * d;
      }

      const at = typeOrder.indexOf(typeof av);
      const bt = typeOrder.indexOf(typeof bv);

      if (at !== bt) {
        return at > bt ? d : -d;
      }

      return (av as any) > (bv as any) ? d : -d;
    }

    return 0;
  });
}

function normalize(x: unknown) {
  if (x instanceof Date) {
    return x.getTime();
  }

  if (typeof x === 'bigint') {
    return Number(x);
  }

  if (x === null) {
    return undefined;
  }

  if (!typeOrder.includes(typeof x)) {
    return {};
  }

  return x;
}
